import { Wrench, ShieldCheck, Clock, PhoneCall, Mail, MessageCircle, Sparkles } from "lucide-react";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import RepairIntakeForm from "@/components/RepairIntakeForm";
import FeaturedReviews from "@/components/FeaturedReviews";
import FAQSection from "@/components/FAQSection";
import { useLang } from "@/context/LanguageContext";

const WHATSAPP_NUMBER = "628990321333";
const WHATSAPP_MESSAGE = "Hai, selamat hari yang baik! Saya punya pertanyaan akan layanan/produk dari Mobile Xpressed. Tolong dibantu, kak";
const WHATSAPP_URL = `https://wa.me/${WHATSAPP_NUMBER}?text=${encodeURIComponent(WHATSAPP_MESSAGE)}`;

export default function RepairServices() {
  const { t } = useLang();
  const services = [
    { icon: Wrench, titleKey: "services.svcBatteryTitle", descKey: "services.svcBatteryDesc", key: "battery" },
    { icon: Sparkles, titleKey: "services.svcCleanTitle", descKey: "services.svcCleanDesc", key: "clean" },
    { icon: ShieldCheck, titleKey: "services.svcDiagTitle", descKey: "services.svcDiagDesc", key: "diagnostics" },
    { icon: Clock, titleKey: "services.svcTurnTitle", descKey: "services.svcTurnDesc", key: "turnaround" },
  ];

  return (
    <main data-testid="services-page" className="max-w-7xl mx-auto px-6 md:px-10 py-14">
      <div className="grid md:grid-cols-12 gap-10 items-center">
        <div className="md:col-span-6 animate-fade-up">
          <div className="text-xs font-bold uppercase tracking-[0.2em] text-brand">{t("services.badge")}</div>
          <h1 className="mt-3 font-display font-extrabold text-4xl sm:text-5xl lg:text-6xl tracking-tighter text-zinc-900 leading-[1.05]">
            {t("services.title1")}
            <br /> {t("services.title2")}
          </h1>
          <p className="mt-5 text-lg text-zinc-600 leading-relaxed max-w-lg">
            {t("services.subtitle")}
          </p>
          <div className="mt-8 flex flex-wrap gap-3">
            <a href="#repair-intake">
              <Button data-testid="hero-intake-btn" className="rounded-full bg-brand hover:bg-brand-hover text-white px-6 py-6 text-base font-semibold">
                {t("services.bookRepair")}
              </Button>
            </a>
            <a href={WHATSAPP_URL} target="_blank" rel="noopener noreferrer">
              <Button data-testid="whatsapp-contact-btn" variant="outline" className="rounded-full border-2 border-zinc-200 hover:border-zinc-900 bg-white text-zinc-900 px-6 py-6 text-base font-semibold">
                <MessageCircle className="w-4 h-4 mr-2" /> {t("services.orContact")}
              </Button>
            </a>
          </div>
        </div>

        <div className="md:col-span-6">
          <div className="rounded-[2rem] overflow-hidden product-image-bg aspect-square max-w-md ml-auto shadow-[0_30px_80px_-20px_rgba(0,0,0,0.15)]">
            <img
              src="https://images.unsplash.com/photo-1550041473-d296a3a8a18a?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA0MTJ8MHwxfHNlYXJjaHwxfHxlbGVjdHJvbmljcyUyMHJlcGFpcnxlbnwwfHx8fDE3ODYzMjU1MjF8MA&ixlib=rb-4.1.0&q=85"
              alt="Repair services"
              className="w-full h-full object-cover"
            />
          </div>
        </div>
      </div>

      <section className="mt-20">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          {services.map((s) => (
            <div key={s.key} className="bg-white rounded-3xl border border-zinc-100 p-6" data-testid={`service-${s.key}`}>
              <div className="w-11 h-11 rounded-xl bg-[#FBE8EC] text-brand flex items-center justify-center">
                <s.icon className="w-5 h-5" />
              </div>
              <div className="mt-4 font-display font-bold text-lg text-zinc-900">{t(s.titleKey)}</div>
              <div className="mt-1.5 text-sm text-zinc-500 leading-relaxed">{t(s.descKey)}</div>
            </div>
          ))}
        </div>
      </section>

      <section className="mt-20 rounded-3xl bg-zinc-900 text-white p-10 md:p-16 grid md:grid-cols-2 gap-10 items-center">
        <div>
          <div className="text-xs font-bold uppercase tracking-[0.2em] text-brand">{t("services.getInTouch")}</div>
          <h3 className="mt-3 font-display font-extrabold text-3xl md:text-4xl tracking-tight leading-tight">{t("services.notSure")}</h3>
          <p className="mt-4 text-zinc-300 leading-relaxed max-w-md">
            {t("services.notSureCopy")}
          </p>
        </div>
        <div className="space-y-3">
          <a href={WHATSAPP_URL} target="_blank" rel="noopener noreferrer" className="flex items-center gap-3 bg-white/5 hover:bg-white/10 transition-colors rounded-2xl px-5 py-4" data-testid="contact-whatsapp">
            <PhoneCall className="w-5 h-5 text-brand" />
            <div>
              <div className="text-xs uppercase tracking-[0.2em] text-zinc-400">{t("services.whatsapp")}</div>
              <div className="font-semibold">+62 (8990) 321 333</div>
            </div>
          </a>
          <a href="mailto:hello@mobilexpressed.com" className="flex items-center gap-3 bg-white/5 hover:bg-white/10 transition-colors rounded-2xl px-5 py-4" data-testid="contact-email">
            <Mail className="w-5 h-5 text-brand" />
            <div>
              <div className="text-xs uppercase tracking-[0.2em] text-zinc-400">{t("services.emailLabel")}</div>
              <div className="font-semibold">hello@mobilexpressed.com</div>
            </div>
          </a>
          <div className="flex items-center gap-3 bg-white/5 rounded-2xl px-5 py-4">
            <MessageCircle className="w-5 h-5 text-brand" />
            <div>
              <div className="text-xs uppercase tracking-[0.2em] text-zinc-400">{t("services.chat")}</div>
              <div className="font-semibold">{t("services.chatHours")}</div>
            </div>
          </div>
        </div>
      </section>

      {/* Repair intake form */}
      <section id="repair-intake" className="mt-20 grid md:grid-cols-12 gap-10">
        <div className="md:col-span-5">
          <div className="text-xs font-bold uppercase tracking-[0.2em] text-brand">{t("services.startRepair")}</div>
          <h2 className="mt-3 font-display font-extrabold text-3xl md:text-4xl tracking-tighter text-zinc-900 leading-tight">
            {t("services.startHead")}
          </h2>
          <p className="mt-4 text-zinc-600 leading-relaxed">
            {t("services.startCopy")}
          </p>
          <ul className="mt-6 space-y-2 text-sm text-zinc-600">
            <li>{t("services.startPt1")}</li>
            <li>{t("services.startPt2")}</li>
            <li>{t("services.startPt3")}</li>
          </ul>
        </div>
        <div className="md:col-span-7">
          <RepairIntakeForm />
        </div>
      </section>

      {/* Customer reviews for repair services */}
      <FeaturedReviews category="Repair Services" limit={6} minRating={4} />

      {/* FAQ */}
      <FAQSection />

      <div className="mt-14 text-center">
        <Link to="/shop" className="text-brand font-semibold hover:underline">{t("services.orBrowse")}</Link>
      </div>
    </main>
  );
}
